/*
 * Copyright [ 2020 - 2024 ] [Matthew Buckton]
 * Copyright [ 2024 - 2025 ] [Maps Messaging B.V.]
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

"use client";

import {useAuth0} from "@auth0/auth0-react";
import {useRouter} from "next/navigation";
import LoginButton from "@/component/auth/login-button";
import Image from "next/image";
import {Alert, AppBar, Box, Button, Container, Toolbar, Typography} from "@mui/material";

export default function Home() {
    const { user, isLoading } = useAuth0();
    const router = useRouter();

    // Redirect to dashboard immediately if authenticated
    if (user) {
        router.push("/dashboard/client");
        return null;
    }

    if (isLoading) return <p>Loading...</p>;

    return (
        <div className="flex flex-col items-center min-h-screen p-8 sm:p-20 font-[family-name:var(--font-geist-sans)] gap-10">
            {/* Top Banner */}
            <AppBar position="static" sx={{ backgroundColor: "#1976d2", padding: 1, width: "100%" }}>
                <Toolbar>
                    <Image src="/simple_logo.jpg" alt="Logo" width={40} height={40} style={{ marginRight: 10 }} />
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>Maps Messaging Admin Client</Typography>
                </Toolbar>
            </AppBar>

            {/* Welcome Section */}
            <Container maxWidth="md" sx={{ textAlign: "center" }}>
                <Typography variant="h6">Welcome to the Maps Messaging Admin Client</Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    This administration panel allows you to manage server licenses, customer accounts, and configuration settings.
                    Use this tool to oversee license assignments, monitor server status, and configure system parameters.
                </Typography>
            </Container>

            {/* Login & Warning Section */}
            <Container maxWidth="sm">
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
                    <Alert severity="warning" sx={{ width: "100%" }}>
                        Authorized users only. Unauthorized access is prohibited.
                    </Alert>
                    <Button variant="contained" color="primary">
                        <LoginButton />
                    </Button>
                </Box>
            </Container>
        </div>
    );
}
